import React from 'react';
import { Meta, User } from '../../../graphql/graphql';
import styles from './../PatientProfileV2.module.scss';
import FirstUpper from '../../FirstUpper';
import FormattedDate from '../../FormattedDate';
import NoEscape from '../../NoEscape';
import TermItemText, { TermItemType } from '../../TermItem/TermItemText';
import useTranslation from '../../../Hooks/useTranslation';
import PatientProfileMouNumber from './PatientProfileMouNumber';
import PatientProfileInvitations from './PatientProfileInvitations';
import PatientProfilePersonActingOnBehalfOfAPatient from './PatientProfilePersonActingOnBehalfOfAPatient';
import usePatientMeta from '../Hooks/usePatientMeta';
import { useAppContext } from '../../../Services/AppContext';
import FormattedMomentDate, {
	DateFormat,
} from '../../../Modules/Base/Components/FormattedMomentDate/FormattedMomentDate';
import usePatientIdentification from '../../../Modules/Patient/Hooks/usePatientIdentification';
import Text from '../../Base/Text/Text';
import PatientProfilePart from './PatientProfilePart';
import useCapitation from '../../../Modules/Workplace/Hooks/useCapitation';
import InsuranceCompanyDisplay from '../../InsuranceCompany/InsuranceCompanyDisplay';
import { usePatientVerification } from '../../../Modules/Patient/Hooks/usePatientVerification';
import PatientBankIdVerifiedBadge from '../../../Modules/Patient/Components/PatientVerificationBadge/PatientBankIdVerifiedBadge';
import { getUsersVzpSupplementaryInsuranceType } from '../../../Modules/User/Services/userMeta.service';

export interface UserEditedMeta extends Omit<User, 'meta'> {
	meta: Record<string, any>;
	originalMeta: Meta[];
}

interface Props {
	user: UserEditedMeta;
	showCzechServices: boolean;
	psychoQuestionnaire?: any;
	psychoQuestionnaireTheme?: string | null;
	setIsLoading: (isLoading: boolean) => void;
	workplaceId: string;
}

const PatientProfileInformation: React.FC<Props> = ({
	user,
	showCzechServices,
	psychoQuestionnaire,
	psychoQuestionnaireTheme,
	setIsLoading,
	workplaceId,
}) => {
	const { t } = useTranslation();
	const insuranceCompany = user.profile?.insuranceCompanyTermItem;
	const userPartner: number | null = user.partner
		? parseInt(user.partner)
		: null;

	const { metaIsLoading, patientDiagnosis } = usePatientMeta(
		user.id,
		user.originalMeta,
	);
	const { isLatam } = useAppContext();
	const { identification } = usePatientIdentification(user);
	const { isCapitationAllowed } = useCapitation(workplaceId);

	const { verification } = usePatientVerification(user);

	return (
		<>
			{user?.profile?.birthDate && (
				<div className={styles.item}>
					<div className={styles.name}>
						<FirstUpper>{t('base.shared.dateOfBirth')}</FirstUpper>:
					</div>
					<div className={styles.text}>
						<FormattedMomentDate
							date={user.profile.birthDate}
							format={DateFormat.DATE}
						/>{' '}
						{!isLatam && identification.value && (
							<>
								<span>{t(identification.title)}</span> {identification.value}
							</>
						)}
					</div>
					<PatientBankIdVerifiedBadge verified={verification.birthNumber} />
				</div>
			)}
			{identification.extra?.insuranceNumber &&
				identification.extra?.showInsuranceNumber && (
					<PatientProfilePart
						label='base.shared.insuranceNumber'
						value={identification.extra?.insuranceNumber}
					/>
				)}
			<div className={styles.item}>
				<div className={styles.name}>
					<FirstUpper>{t('base.shared.address')}</FirstUpper>:
				</div>
				<div className={styles.text}>
					{showCzechServices && user.csszResult && (
						<span>
							{user.csszResult.result ? (
								<img
									src='/dist/icons/v2/badge-check.svg'
									alt='test'
									className={styles.icon}
								/>
							) : null}
							<FirstUpper>
								{user.csszResult.result
									? t('base.verified')
									: t('component.csszResult.addressNotVerified')}
							</FirstUpper>{' '}
							<FormattedDate date={user.csszResult.checkedAt} />
						</span>
					)}{' '}
					{user.profile?.address?.fullAddress}
				</div>
				<PatientBankIdVerifiedBadge verified={verification.address} />
			</div>
			{user.formattedPhoneNumber && (
				<div className={styles.item}>
					<div className={styles.name}>
						<FirstUpper>{t('base.shared.phone')}</FirstUpper>:
					</div>
					<div className={styles.text}>{user.formattedPhoneNumber}</div>
					<PatientBankIdVerifiedBadge verified={verification.phoneNumber} />
				</div>
			)}
			{user.email && (
				<div className={styles.item}>
					<div className={styles.name}>
						<FirstUpper>{t('base.shared.email')}</FirstUpper>:
					</div>
					<div className={styles.text}>
						<a href={'mailto:' + user.email}>{user.email}</a>
					</div>
					<PatientBankIdVerifiedBadge verified={verification.email} />
				</div>
			)}
			{(insuranceCompany || user.vzpResult) && (
				<div className={styles.item}>
					<div className={styles.name}>
						<FirstUpper>{t('base.insuranceShort')}</FirstUpper>:
					</div>
					<div className={styles.text}>
						{' '}
						{showCzechServices && user.vzpResult && (
							<span>
								{user.vzpResult.result ? (
									<img
										src='/dist/icons/v2/badge-check.svg'
										alt='test'
										className={styles.icon}
									/>
								) : null}
								<FirstUpper>
									{user.vzpResult.result
										? t('base.verified')
										: t('component.vzpResult.insuranceNotVerified')}
								</FirstUpper>{' '}
								<FormattedDate date={user.vzpResult.checkedAt} />
							</span>
						)}{' '}
						<InsuranceCompanyDisplay
							insuranceCompanyTermItem={insuranceCompany}
						/>
					</div>
				</div>
			)}
			{getUsersVzpSupplementaryInsuranceType(user) && (
				<div className={styles.item}>
					<div className={styles.name}>
						<FirstUpper>{t('base.vzpSupplementaryInsuranceType')}</FirstUpper>:
					</div>
					<div className={styles.text}>
						<TermItemText
							termItemId={getUsersVzpSupplementaryInsuranceType(user)!}
							termType={TermItemType.VZP_SUPPLEMENTARY_INSURANCE_TYPE}
							labelFormat='{value} - {name}'
						/>
					</div>
				</div>
			)}
			{user.vzpResult?.result?.prubehPojisteni?.druhPojisteni && (
				<div className={styles.item}>
					<div className={styles.name}>
						<FirstUpper>{t('base.typeOfInsurance')}</FirstUpper>:
					</div>
					<div className={styles.text}>
						{user.vzpResult?.result?.prubehPojisteni?.druhPojisteni}
					</div>
				</div>
			)}
			{!user.vzpResult &&
				user?.meta?.insurance &&
				user?.meta?.insurance.hasInsurance && (
					<>
						<div className={styles.item}>
							<div className={styles.name}>
								<FirstUpper>{t('base.shared.insuranceCompany')}</FirstUpper>:
							</div>
							<div className={styles.text}>
								{user.meta.insurance.companyName}
							</div>
						</div>
						<div className={styles.item}>
							<div className={styles.name}>
								<FirstUpper>{t('base.typeOfInsurance')}</FirstUpper>:
							</div>
							<div className={styles.text}>{user.meta.insurance.type}</div>
						</div>
					</>
				)}
			{userPartner === 3 && <PatientProfileMouNumber user={user} />}
			<div className={styles.item}>
				<div className={styles.name}>
					<FirstUpper>{t('base.registrationDate')}</FirstUpper>:
				</div>
				<div className={styles.text}>
					<FormattedMomentDate
						date={user.createdAt}
						format={DateFormat.DATE_TIME}
					/>
				</div>
			</div>
			{psychoQuestionnaire && userPartner === 3 && (
				<div className={styles.item}>
					<div className={styles.name}></div>
					<div className={styles.text}>
						<div className={'text-' + psychoQuestionnaireTheme}>
							<i
								className={
									'fa fa-' +
									psychoQuestionnaire?.fontIcon +
									' fa-fw mr-1 text-' +
									psychoQuestionnaireTheme
								}></i>
							{psychoQuestionnaire.title}
						</div>
						<NoEscape children={psychoQuestionnaire?.description || ''} />
					</div>
				</div>
			)}
			{user.profile?.formattedGender && (
				<div className={styles.item}>
					<div className={styles.name}>
						<FirstUpper>{t('base.shared.gender')}</FirstUpper>:
					</div>
					<div className={styles.text}>{user.profile.formattedGender}</div>
				</div>
			)}
			{user.meta?.historyClinicNumber && (
				<div className={styles.item}>
					<div className={styles.name}>
						<FirstUpper>{t('base.medicalHistoryNumber')}</FirstUpper>:
					</div>
					<div className={styles.text}>{user.meta.historyClinicNumber}</div>
				</div>
			)}
			{user.meta?.identificationDocument?.type && (
				<div className={styles.item}>
					<div className={styles.name}>
						<FirstUpper>
							{t('base.label.identificationDocumentType')}
						</FirstUpper>
						:
					</div>
					<div className={styles.text}>
						<TermItemText
							termItemId={user.meta.identificationDocument.type}
							termType={TermItemType.IDENTIFICATION_DOCUMENT_TYPE}
						/>
					</div>
				</div>
			)}
			{user.meta?.identificationDocument?.number && (
				<div className={styles.item}>
					<div className={styles.name}>
						<FirstUpper>{t('base.personalIdentificationNumber')}</FirstUpper>:
					</div>
					<div className={styles.text}>
						{user.meta.identificationDocument.number}
					</div>
				</div>
			)}
			{user.profile?.nationalityTermItem && (
				<div className={styles.item}>
					<div className={styles.name}>
						<FirstUpper>{t('base.citizenship')}</FirstUpper>:
					</div>
					<div className={styles.text}>
						{user.profile.nationalityTermItem.name}
					</div>
				</div>
			)}
			{user.meta?.emergencyContact?.name && (
				<div className={styles.item}>
					<div className={styles.name}>
						<FirstUpper>
							{t('diabetes.patientsInfo.emergencyContact.name')}
						</FirstUpper>
						:
					</div>
					<div className={styles.text}>
						{user.meta.emergencyContact.name}
						{user.meta.emergencyContact?.phoneNumber
							? ' (' + user.meta.emergencyContact.phoneNumber + ')'
							: ''}
					</div>
				</div>
			)}
			{user.workplaceMember?.archivedBy?.profile?.fullName && (
				<div className={styles.item}>
					<Text
						className={styles.name}
						tx='base.profileArchived'
						colon
						firstUpper
					/>
					<div className={styles.text}>
						<FormattedMomentDate
							date={user.workplaceMember?.archivedAt}
							format={DateFormat.DATE}
						/>{' '}
						({user.workplaceMember.archivedBy.profile.fullName})
					</div>
				</div>
			)}
			{user.meta.birthPlace && (
				<PatientProfilePart
					label='component.patientManualRegistrationHungaryForm.birthPlace'
					value={user.meta.birthPlace}
				/>
			)}
			{user.meta.mothersFullname && (
				<PatientProfilePart
					label='component.patientManualRegistrationHungaryForm.mothersFullname'
					value={user.meta.mothersFullname}
				/>
			)}
			{user.note && (
				<PatientProfilePart label='base.shared.note' value={user.note} />
			)}
			{isCapitationAllowed && (
				<div className={styles.item}>
					<Text
						className={styles.name}
						tx='base.capitationRegistration'
						colon
						firstUpper
					/>
					<div className={styles.text}>
						{user.profile?.capitationRegistrationAt ? (
							<FormattedMomentDate
								date={user.profile?.capitationRegistrationAt}
								format={DateFormat.DATE}
							/>
						) : (
							<Text tx='base.no' firstUpper />
						)}
					</div>
				</div>
			)}
			{user.meta.identificationCode && (
				<PatientProfilePart
					label='base.identificationCode'
					value={user.meta.identificationCode}
				/>
			)}
			{(!userPartner || (userPartner && userPartner != 3)) &&
				user?.workplaceMember?.status !== 'ACCEPTED' && (
					<PatientProfileInvitations user={user} />
				)}
			{userPartner === 3 && (
				<PatientProfilePersonActingOnBehalfOfAPatient
					patientId={user.id}
					patientMeta={user.originalMeta}
				/>
			)}
			{!metaIsLoading && patientDiagnosis?.mainDiagnosis && (
				<div className={styles.item}>
					<div className={styles.name}>
						<FirstUpper>{t('base.mainDiagnosis')}</FirstUpper>:
					</div>
					<div className={styles.text}>
						{patientDiagnosis?.mainDiagnosis?.label}
					</div>
				</div>
			)}
			{/*{user.profile?.languageFormatted && (*/}
			{/*	<div className={styles.item} style={{ marginTop: '20px' }}>*/}
			{/*		<Text*/}
			{/*			className={styles.name}*/}
			{/*			firstUpper*/}
			{/*			colon*/}
			{/*			tx='base.patientsLanguage'*/}
			{/*		/>*/}
			{/*		<div className={styles.text}>*/}
			{/*			<PatientLanguageBadge user={user} />*/}
			{/*		</div>*/}
			{/*	</div>*/}
			{/*)}*/}
		</>
	);
};

export default PatientProfileInformation;
